import type { MessageDescriptor } from "@lingui/core";

import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { observable } from "mobx";
import { observer } from "mobx-react-lite";
import { useMemo } from "react";

import {
  ChangeThresholdsType,
  DefaultAlertThreshold,
  type PostApiV1ThresholdsData,
  ThresholdTables,
  ThresholdTriggers,
  ThresholdTypes,
  apiPostThresholds,
} from "../../../api";
import { AppModel } from "../../../app/app-model";
import { Button } from "../../../components/ui-kit/button";
import { Dialog } from "../../../components/ui-kit/dialog";
import { NumberInput } from "../../../components/ui-kit/fields/number-input";
import { Select } from "../../../components/ui-kit/fields/select";
import { TextInput } from "../../../components/ui-kit/fields/text-input";
import { resolveEnumLabel } from "../../../components/ui-kit/fields/utils";
import { Form } from "../../../components/ui-kit/form/form";
import { FormRow } from "../../../components/ui-kit/form/form-row";
import { FormTable } from "../../../components/ui-kit/form/form-table";
import {
  RowAccordion,
  useRowAccordion,
} from "../../../components/ui-kit/tables/row-accordion";
import { assert, createRowId } from "../../../utils/utils";

export const AddThresholdsDialog = observer(function AddThresholdsDialog({
  app,
  defaultThresholds,
  onClose,
  onSaved,
}: {
  app: AppModel;
  defaultThresholds: DefaultAlertThreshold[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useLingui();
  const patient = app.requirePatient();
  const api = app.seerlinqApi;

  const availableThresholds = useMemo(() => {
    const map: Record<string, ThresholdTables> = {};
    for (const dt of defaultThresholds) {
      map[dt.threshold_variable] = dt.threshold_variable_table;
    }
    return map;
  }, [defaultThresholds]);

  const accordion = useRowAccordion(createRow, [createRow()]);

  const hasValidRows =
    accordion.rows.length > 0 &&
    accordion.rows.every((r) => isThresholdRowComplete(r));

  async function save() {
    const items = accordion.rows.map((row) => ({
      rowId: row.__rowId,
      payload: toThresholdPayload(
        row,
        patient.patientId,
        availableThresholds[row.threshold_variable ?? ""],
      ),
    }));

    let succeededCount = 0;
    let firstError: unknown = null;
    for (const { rowId, payload } of items) {
      try {
        await apiPostThresholds({ body: payload });
        accordion.removeRow(rowId);
        succeededCount += 1;
      } catch (error) {
        // Keep going so one bad row (e.g. a duplicate that violates the
        // one-per-variable/trigger/type/settings rule) doesn't block the valid
        // rows after it; the failed rows stay in the dialog to fix and retry.
        firstError ??= error;
      }
    }

    if (succeededCount > 0) {
      api.clearResponseCache();
      onSaved();
    }

    if (firstError != null) {
      // Surface a single dialog (the service shows one at a time) rather than
      // one per failed row; the rows left behind show what still needs fixing.
      window.handleError(firstError);
    } else {
      onClose();
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      className="top-[5dvh] max-h-[90dvh] w-[750px] translate-y-0"
    >
      <h3 className="mb-6 text-base font-bold">
        <Trans>Add thresholds</Trans>
      </h3>

      <div className="mb-4 text-sm">
        <div className="text-danger-foreground">
          <Trans>
            Only one threshold per patient, per variable, per trigger, per type,
            and per threshold settings can exist. When you want to update a
            specific threshold, do it in the table below.
          </Trans>
        </div>
        <div className="mt-2">
          <Trans>
            <strong>Value-based thresholds</strong>: Alerts are triggered when
            the variable is above/below (based on the threshold type) at least{" "}
            <em>X</em> times out of <em>Y</em>.
            <br />
            <strong>Change-based thresholds</strong>: Alerts are triggered when
            the variable increased/decreased (based on change type) over{" "}
            <em>X</em> day(s) (while taking into an account <em>N</em> maximum
            measurements). When defining threshold for <strong>decrease</strong>
            , the value must be negative; if you insert positive number, it is
            automatically treated as negative.
          </Trans>
        </div>
      </div>

      <Form className="flex min-h-0 flex-1 flex-col" onSubmit={() => save()}>
        <div className="min-h-0 overflow-y-auto overflow-x-hidden py-1 px-1">
          <RowAccordion
            {...accordion}
            summaryLabel={(row, index) =>
              row.threshold_variable
                ? `${row.threshold_variable} (${resolveEnumLabel(threshTables[availableThresholds[row.threshold_variable]])})`
                : t`Threshold ${index + 1}`
            }
            renderRow={(row) => (
              <RowForm row={row} availableThresholds={availableThresholds} />
            )}
          />
        </div>

        <div className="mt-6 flex justify-center gap-2">
          <Button type="submit" disabled={!hasValidRows}>
            <Trans>Add</Trans>
          </Button>
          <Button
            type="button"
            variant="subtle"
            size="default"
            onClick={onClose}
          >
            <Trans>Cancel</Trans>
          </Button>
        </div>
      </Form>
    </Dialog>
  );
});

const RowForm = observer(function RowForm({
  row,
  availableThresholds,
}: {
  row: ThresholdRow;
  availableThresholds: Record<string, ThresholdTables>;
}) {
  const { t } = useLingui();
  return (
    <FormTable dataAdd className="max-w-full">
      <FormRow
        label={t`Table`}
        data={
          <span>
            {row.threshold_variable
              ? resolveEnumLabel(
                  threshTables[availableThresholds[row.threshold_variable]],
                ) || "--"
              : "--"}
          </span>
        }
      />
      <FormRow
        label={t`Variable`}
        data={
          <Select
            model={row}
            attr="threshold_variable"
            values={Object.keys(availableThresholds)}
          />
        }
        mandatory
      />
      <FormRow
        label={t`Type`}
        data={
          <Select
            model={row}
            attr="threshold_type"
            values={threshTypes}
            disabled={row.threshold_trigger === ThresholdTriggers.change_based}
          />
        }
        mandatory={row.threshold_trigger === ThresholdTriggers.value_based}
      />
      <FormRow
        label={t`Trigger`}
        data={
          <Select
            model={row}
            attr="threshold_trigger"
            values={threshTriggers}
            onChange={() => initSettings(row)}
          />
        }
        mandatory
      />
      <FormRow
        label={t`Settings`}
        data={<SettingsFields row={row} />}
        mandatory
      />
      <FormRow
        label={t`Value`}
        data={
          <NumberInput
            value={row.threshold_value ?? undefined}
            onChange={(v) => {
              row.threshold_value = normalizeThresholdValue(v ?? null, row);
            }}
          />
        }
        mandatory
      />
      <FormRow
        label={t`Comment`}
        data={
          <TextInput
            model={row}
            attr="threshold_comment"
            multiline="autosize"
            width={330}
          />
        }
      />
    </FormTable>
  );
});

const SettingsFields = observer(function SettingsFields({
  row,
}: {
  row: ThresholdRow;
}) {
  const settings = row.threshold_settings;

  if (!settings) {
    return <span>--</span>;
  }

  if (settings.threshold_trigger === "value_based") {
    return (
      <div className="flex items-center gap-1">
        <NumberInput
          value={settings.at_least ?? undefined}
          onChange={(v) => {
            settings.at_least = v ?? null;
          }}
          width={40}
        />
        <span>
          {" "}
          <Trans>out of</Trans>{" "}
        </span>
        <NumberInput
          value={settings.out_of ?? undefined}
          onChange={(v) => {
            settings.out_of = v ?? null;
          }}
          width={40}
        />
      </div>
    );
  }

  if (settings.threshold_trigger === "change_based") {
    return (
      <div className="flex flex-wrap items-center gap-1">
        <Select
          value={settings.change_type ?? undefined}
          onChange={(v) => {
            settings.change_type = v as ChangeThresholdsType;
            row.threshold_type = changeToThreshType[v];
            row.threshold_value = normalizeThresholdValue(
              row.threshold_value,
              row,
            );
          }}
          values={changeTypes}
        />
        <span>
          {" "}
          <Trans>over</Trans>{" "}
        </span>
        <NumberInput
          value={settings.look_back_days ?? undefined}
          onChange={(v) => {
            settings.look_back_days = v ?? null;
          }}
          width={40}
        />
        <span>
          {" "}
          <Trans>day(s) (max</Trans>{" "}
        </span>
        <NumberInput
          value={settings.maximum_measurements ?? undefined}
          onChange={(v) => {
            settings.maximum_measurements = v ?? null;
          }}
          width={40}
        />
        <span>
          {" "}
          <Trans>measurements)</Trans>
        </span>
      </div>
    );
  }

  return <span>--</span>;
});

const threshTables: Record<string, MessageDescriptor> = {
  medicaldata: msg`Medical data (Manual)`,
  symptoms: msg`Symptoms (Manual)`,
  ppg_derived: msg`PPG-derived (from Oximeter)`,
  sq_computed: msg`SQ-computed (our algorithm)`,
};

const threshTypes: Record<string, MessageDescriptor> = {
  low: msg`low`,
  high: msg`high`,
};

const threshTriggers: Record<string, MessageDescriptor> = {
  value_based: msg`Value-based`,
  change_based: msg`Change-based`,
};

const changeTypes: Record<string, MessageDescriptor> = {
  increase: msg`increase`,
  decrease: msg`decrease`,
};

const changeToThreshType: Record<string, ThresholdTypes> = {
  increase: ThresholdTypes.high,
  decrease: ThresholdTypes.low,
};

type ThresholdSettings =
  | {
      threshold_trigger: "value_based";
      at_least: number | null;
      out_of: number | null;
    }
  | {
      threshold_trigger: "change_based";
      change_type: ChangeThresholdsType | null;
      look_back_days: number | null;
      maximum_measurements: number | null;
    };

type ThresholdRow = {
  __rowId: string;
  threshold_variable: string | null;
  threshold_trigger: ThresholdTriggers | null;
  threshold_type: ThresholdTypes | null;
  threshold_settings: ThresholdSettings | null;
  threshold_value: number | null;
  threshold_comment: string | null;
};

function createRow(): ThresholdRow {
  return observable({
    __rowId: createRowId(),
    threshold_variable: null,
    threshold_trigger: null,
    threshold_type: null,
    threshold_settings: null,
    threshold_value: null,
    threshold_comment: null,
  });
}

function isThresholdSettingsComplete(
  settings: ThresholdSettings | null,
): boolean {
  if (!settings) {
    return false;
  }
  if (settings.threshold_trigger === "value_based") {
    return settings.at_least != null && settings.out_of != null;
  }
  return (
    settings.change_type != null &&
    settings.look_back_days != null &&
    settings.maximum_measurements != null
  );
}

// Single source of truth for the mandatory-field contract. Threshold type is
// user-selected for value-based triggers but auto-derived for change-based.
function isThresholdRowComplete(row: ThresholdRow): boolean {
  if (
    !row.threshold_variable ||
    !row.threshold_trigger ||
    row.threshold_value == null
  ) {
    return false;
  }
  if (!isThresholdSettingsComplete(row.threshold_settings)) {
    return false;
  }
  if (row.threshold_trigger === ThresholdTriggers.value_based) {
    return !!row.threshold_type;
  }
  return true;
}

function isDecreaseChangeBased(row: ThresholdRow): boolean {
  const settings = row.threshold_settings;
  return (
    settings?.threshold_trigger === "change_based" &&
    settings.change_type === ChangeThresholdsType.decrease
  );
}

// Decrease thresholds are stored as negative. Mirror that in the field as the
// user types so the displayed value always matches what gets persisted, rather
// than flipping the sign invisibly at submit time.
function normalizeThresholdValue(
  value: number | null,
  row: ThresholdRow,
): number | null {
  if (value != null && value > 0 && isDecreaseChangeBased(row)) {
    return -value;
  }
  return value;
}

function toThresholdSettingsPayload(
  settings: ThresholdSettings,
): PostApiV1ThresholdsData["body"]["threshold_settings"] {
  if (settings.threshold_trigger === "value_based") {
    const { at_least, out_of } = settings;
    assert(at_least != null && out_of != null);
    return { threshold_trigger: "value_based", at_least, out_of };
  }
  const { change_type, look_back_days, maximum_measurements } = settings;
  assert(
    change_type != null &&
      look_back_days != null &&
      maximum_measurements != null,
  );
  return {
    threshold_trigger: "change_based",
    change_type,
    look_back_days,
    maximum_measurements,
  };
}

function toThresholdPayload(
  row: ThresholdRow,
  patientId: number,
  variableTable: ThresholdTables,
): PostApiV1ThresholdsData["body"] {
  const {
    threshold_variable,
    threshold_trigger,
    threshold_type,
    threshold_value,
    threshold_settings,
  } = row;
  // Submission is gated by isThresholdRowComplete (the Add button stays disabled
  // until every row is valid). Asserting the invariant narrows the nullable
  // fields, so the payload is built with no casts and the compiler checks it
  // against the body type (e.g. a new required field would be caught).
  assert(
    threshold_variable != null &&
      threshold_trigger != null &&
      threshold_type != null &&
      threshold_value != null &&
      threshold_settings != null,
  );
  return {
    threshold_variable,
    threshold_trigger,
    threshold_type,
    threshold_settings: toThresholdSettingsPayload(threshold_settings),
    threshold_value,
    threshold_comment: row.threshold_comment,
    threshold_variable_table: variableTable,
    patient_id: patientId,
  };
}

function initSettings(item: ThresholdRow) {
  if (item.threshold_trigger === ThresholdTriggers.value_based) {
    item.threshold_settings = observable({
      threshold_trigger: "value_based" as const,
      at_least: null,
      out_of: null,
    });
  } else {
    item.threshold_type = null;
    item.threshold_settings = observable({
      threshold_trigger: "change_based" as const,
      change_type: null,
      look_back_days: null,
      maximum_measurements: null,
    });
  }
}
